import React, { useEffect, useState } from 'react';
import { FileText, Download, Eye, X, Copy, CircleCheck as CheckCircle2, ChevronLeft, ChevronRight, ThumbsUp, ThumbsDown, TriangleAlert as AlertTriangle, CreditCard as Edit2, Save, Link as LinkIcon, Loader, ShieldBan } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface Installment {
  id: string;
  due_date: string;
  amount: number;
  status: string;
}

interface ContractFile {
  id: string;
  file_name: string;
  file_url: string;
  file_type: string;
}

interface Contract {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  email: string;
  address: string;
  business_address: string;
  pix_key: string;
  bank: string;
  relative_phones: { numero: string; relacao: string }[];
  loan_amount: number;
  installments: number;
  total_amount: number;
  interest_rate: number;
  ruta: number;
  approval_status: string;
  observations: string | null;
  created_at: string;
  contract_files: ContractFile[];
  installments_data: Installment[];
  // link info (joined)
  link_id?: string | null;
}

interface EditableFields {
  client_name: string;
  cpf: string;
  phone: string;
  email: string;
  address: string;
  business_address: string;
  pix_key: string;
  bank: string;
}

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const formatCPF = (cpf: string) => {
  const n = cpf.replace(/\D/g, '');
  return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatPhone = (phone: string) => {
  const n = phone.replace(/\D/g, '');
  return n.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

export default function VendorReview() {
  const { user } = useAuth();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Contract | null>(null);

  // file viewer
  const [fileIndex, setFileIndex] = useState(0);
  const [fileErrors, setFileErrors] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);

  // editing
  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState<EditableFields | null>(null);
  const [saving, setSaving] = useState(false);

  // observations
  const [editingObs, setEditingObs] = useState(false);
  const [obsText, setObsText] = useState('');
  const [savingObs, setSavingObs] = useState(false);

  // approve
  const [approving, setApproving] = useState(false);

  // reject modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // new link modal (shown after rejection)
  const [showNewLinkModal, setShowNewLinkModal] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState<string | null>(null);
  const [newLinkCopied, setNewLinkCopied] = useState(false);
  const [rejectedContract, setRejectedContract] = useState<Contract | null>(null);
  const [blacklistInfo, setBlacklistInfo] = useState<{ client_name: string; reason: string; created_at: string } | null>(null);

  useEffect(() => {
    fetchContracts();
  }, [user]);

  const fetchContracts = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('contracts')
        .select(`
          *,
          contract_files(*),
          installments_data:installments(*)
        `)
        .eq('approval_status', 'vendor_review')
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false });

      if (err) throw err;
      setContracts(data || []);
    } catch (e) {
      console.error(e);
      setError('Erro ao carregar cadastros pendentes de revisão.');
    } finally {
      setLoading(false);
    }
  };

  const openContract = (c: Contract) => {
    setSelected(c);
    setFileIndex(0);
    setFileErrors({});
    setEditing(false);
    setEditFields({
      client_name: c.client_name,
      cpf: c.cpf,
      phone: c.phone,
      email: c.email,
      address: c.address,
      business_address: c.business_address,
      pix_key: c.pix_key,
      bank: c.bank,
    });
    setObsText(c.observations || '');
    setEditingObs(false);
    checkBlacklist(c.cpf);
  };

  const checkBlacklist = async (cpf: string) => {
    const digits = cpf.replace(/\D/g, '');
    const { data } = await supabase
      .from('cpf_blacklist')
      .select('client_name, reason, created_at')
      .eq('cpf', digits)
      .maybeSingle();
    setBlacklistInfo(data || null);
  };

  const handleSaveEdits = async () => {
    if (!selected || !editFields) return;
    setSaving(true);
    try {
      const { error: err } = await supabase
        .from('contracts')
        .update({
          client_name: editFields.client_name,
          cpf: editFields.cpf,
          phone: editFields.phone,
          email: editFields.email,
          address: editFields.address,
          business_address: editFields.business_address,
          pix_key: editFields.pix_key,
          bank: editFields.bank,
        })
        .eq('id', selected.id);
      if (err) throw err;

      const updated = { ...selected, ...editFields };
      setContracts(contracts.map(c => c.id === selected.id ? updated : c));
      setSelected(updated);
      setEditing(false);
    } catch (e) {
      console.error(e);
      setError('Erro ao salvar edições.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveObs = async () => {
    if (!selected) return;
    setSavingObs(true);
    try {
      const { error: err } = await supabase
        .from('contracts')
        .update({ observations: obsText })
        .eq('id', selected.id);
      if (err) throw err;
      const updated = { ...selected, observations: obsText };
      setContracts(contracts.map(c => c.id === selected.id ? updated : c));
      setSelected(updated);
      setEditingObs(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingObs(false);
    }
  };

  const handleApproveContract = async (contract: Contract) => {
    if (!user) return;
    setApproving(true);
    try {
      const { error: err } = await supabase
        .from('contracts')
        .update({
          approval_status: 'pending',
          vendor_reviewed_at: new Date().toISOString(),
        })
        .eq('id', contract.id);
      if (err) throw err;
      setContracts(prev => prev.filter(c => c.id !== contract.id));
      if (selected?.id === contract.id) setSelected(null);
    } catch (e) {
      console.error(e);
      setError('Erro ao aprovar cadastro.');
    } finally {
      setApproving(false);
    }
  };

  const handleApprove = async () => {
    if (!selected) return;
    await handleApproveContract(selected);
  };

  const handleRejectSubmit = async () => {
    if (!selected || !rejectReason.trim()) return;
    setRejecting(true);

    try {
      // 1. Mark contract as vendor_rejected
      const { error: err } = await supabase
        .from('contracts')
        .update({
          approval_status: 'vendor_rejected',
          vendor_reviewed_at: new Date().toISOString(),
          vendor_rejection_reason: rejectReason.trim(),
        })
        .eq('id', selected.id);
      if (err) throw err;

      // 2. Cancel the original registration link (find via self_registrations)
      const { data: reg } = await supabase
        .from('client_self_registrations')
        .select('link_id')
        .eq('contract_id', selected.id)
        .maybeSingle();

      if (reg?.link_id) {
        await supabase
          .from('seller_registration_links')
          .update({
            status: 'cancelled',
            is_active: false,
            vendor_rejection_reason: rejectReason.trim(),
          })
          .eq('id', reg.link_id);
      }

      const contractSnapshot = selected;
      setContracts(contracts.filter(c => c.id !== selected.id));
      setSelected(null);
      setShowRejectModal(false);
      setRejectReason('');
      setRejectedContract(contractSnapshot);
      setShowNewLinkModal(true);
      setNewLinkUrl(null);
    } catch (e) {
      console.error(e);
      setError('Erro ao recusar cadastro.');
    } finally {
      setRejecting(false);
    }
  };

  const handleGenerateNewLink = async () => {
    if (!rejectedContract || !user) return;
    setGeneratingLink(true);
    try {
      // Fetch original link data to clone financial settings
      const { data: reg } = await supabase
        .from('client_self_registrations')
        .select('link_id')
        .eq('contract_id', rejectedContract.id)
        .maybeSingle();

      let linkData: Record<string, unknown> = {
        seller_id: user.id,
        unit_id: user.unit_id,
        slug: crypto.randomUUID(),
        loan_amount: rejectedContract.loan_amount,
        installments: rejectedContract.installments,
        interest_rate: rejectedContract.interest_rate,
        total_amount: rejectedContract.total_amount,
        installment_amount: rejectedContract.total_amount / rejectedContract.installments,
        ruta: rejectedContract.ruta,
        status: 'pending',
        is_active: true,
        seller_name: user.name || user.email,
      };

      if (reg?.link_id) {
        const { data: origLink } = await supabase
          .from('seller_registration_links')
          .select('*')
          .eq('id', reg.link_id)
          .maybeSingle();
        if (origLink) {
          linkData = {
            ...linkData,
            installment_amount: origLink.installment_amount,
            installment_dates: origLink.installment_dates,
            observations: origLink.observations,
          };
        }
      }

      const { data: newLink, error: err } = await supabase
        .from('seller_registration_links')
        .insert(linkData)
        .select()
        .single();
      if (err) throw err;

      const domain = import.meta.env.VITE_LEADS_DOMAIN || window.location.origin;
      const url = `${domain}/cadastro/${newLink.slug}`;
      setNewLinkUrl(url);
    } catch (e) {
      console.error(e);
      setError('Erro ao gerar novo link.');
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyNewLink = () => {
    if (!newLinkUrl) return;
    navigator.clipboard.writeText(newLinkUrl);
    setNewLinkCopied(true);
    setTimeout(() => setNewLinkCopied(false), 2000);
  };

  const getCurrentFile = () => {
    if (!selected || !selected.contract_files.length) return null;
    return selected.contract_files[fileIndex];
  };

  const isImage = (t: string) => t.startsWith('image/');
  const isPDF = (t: string) => t === 'application/pdf';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-600 p-4">{error}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 lg:p-0">
      <div className="max-w-7xl mx-auto space-y-4 lg:space-y-6">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-800">Revisao de Cadastros</h1>
          <p className="text-sm text-gray-500 mt-1">
            Cadastros enviados por clientes aguardando sua revisao antes de ir para o gestor.
          </p>
        </div>

        {contracts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Nenhum cadastro pendente de revisao</p>
            <p className="text-sm text-gray-400 mt-1">
              Quando um cliente concluir o cadastro via link, ele aparecera aqui.
            </p>
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Cliente', 'Empréstimo', 'Total', 'Ruta', 'Data', 'Acoes'].map(h => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contracts.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{c.client_name}</div>
                        <div className="text-xs text-gray-500">{formatCPF(c.cpf)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(c.loan_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(c.total_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        Ruta {c.ruta}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(c.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => openContract(c)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Ver detalhes"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleApproveContract(c)}
                            disabled={approving}
                            className="flex items-center gap-1 px-2 py-1 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors text-xs disabled:opacity-50"
                            title="Aprovar e enviar ao gestor"
                          >
                            <ThumbsUp className="w-3.5 h-3.5" />
                            Aprovar
                          </button>
                          <button
                            onClick={() => { setSelected(c); setShowRejectModal(true); }}
                            className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-xs"
                            title="Recusar cadastro"
                          >
                            <ThumbsDown className="w-3.5 h-3.5" />
                            Recusar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ─── Detail modal ─── */}
        {selected && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-4 lg:p-6 max-w-7xl w-full max-h-[92vh] overflow-y-auto relative">
              <button
                onClick={() => setSelected(null)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="flex items-center justify-between mb-6 pr-10 gap-2 flex-wrap">
                <h2 className="text-lg lg:text-xl font-bold text-gray-800">
                  Revisao: {selected.client_name}
                </h2>
                <div className="flex gap-2">
                  {!editing && (
                    <button
                      onClick={() => setEditing(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      Editar dados
                    </button>
                  )}
                  <button
                    onClick={handleApprove}
                    disabled={approving}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {approving ? <Loader className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
                    {approving ? 'Aprovando...' : 'Aprovar'}
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors"
                  >
                    <ThumbsDown className="w-4 h-4" />
                    Recusar
                  </button>
                </div>
              </div>

              {blacklistInfo && (
                <div className="bg-red-50 border-2 border-red-300 rounded-xl overflow-hidden mb-4">
                  <div className="flex items-center gap-3 px-4 py-3 bg-red-100 border-b border-red-300">
                    <ShieldBan className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-sm font-bold text-red-800">ATENCAO: CPF NA BLACKLIST</p>
                  </div>
                  <div className="px-4 py-3 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-red-700">Nome registrado:</span>
                      <span className="text-sm font-semibold text-red-800">{blacklistInfo.client_name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-red-700">Motivo da exclusao:</span>
                      <span className="text-sm font-semibold text-red-800">{blacklistInfo.reason}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-red-700">Data:</span>
                      <span className="text-sm font-semibold text-red-800">
                        {new Date(blacklistInfo.created_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <p className="text-xs text-red-600 font-medium pt-1">
                      Este CPF foi excluido anteriormente. Avalie com cuidado antes de aprovar.
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left column */}
                <div className="space-y-5">
                  {/* Client data (editable) */}
                  <div className="border-b pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-semibold text-gray-800">Dados do Cliente</h3>
                      {editing && (
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveEdits}
                            disabled={saving}
                            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
                          >
                            <Save className="w-3.5 h-3.5" />
                            {saving ? 'Salvando...' : 'Salvar'}
                          </button>
                          <button
                            onClick={() => { setEditing(false); setEditFields({ client_name: selected.client_name, cpf: selected.cpf, phone: selected.phone, email: selected.email, address: selected.address, business_address: selected.business_address, pix_key: selected.pix_key, bank: selected.bank }); }}
                            className="px-3 py-1 text-gray-600 hover:text-gray-800 rounded-md text-sm hover:bg-gray-100"
                          >
                            Cancelar
                          </button>
                        </div>
                      )}
                    </div>

                    {editing && editFields ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {([
                          ['Nome', 'client_name'],
                          ['CPF', 'cpf'],
                          ['Telefone', 'phone'],
                          ['E-mail', 'email'],
                          ['Endereço Residencial', 'address'],
                          ['Endereço Comercial', 'business_address'],
                          ['Chave PIX', 'pix_key'],
                          ['Banco', 'bank'],
                        ] as [string, keyof EditableFields][]).map(([label, key]) => (
                          <div key={key} className={key === 'address' || key === 'business_address' ? 'lg:col-span-2' : ''}>
                            <label className="block text-xs text-gray-500 mb-1">{label}</label>
                            <input
                              value={editFields[key]}
                              onChange={e => setEditFields({ ...editFields, [key]: e.target.value })}
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <Field label="Nome" value={selected.client_name} />
                        <Field label="CPF" value={formatCPF(selected.cpf)} />
                        <Field label="Telefone" value={formatPhone(selected.phone)} />
                        <Field label="E-mail" value={selected.email} />
                        <Field label="End. Residencial" value={selected.address} span />
                        <Field label="End. Comercial" value={selected.business_address} span />
                        <Field label="Chave PIX" value={selected.pix_key} />
                        <Field label="Banco" value={selected.bank} />
                      </div>
                    )}

                    {selected.relative_phones?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs text-gray-500 mb-2">Contatos de Emergência</p>
                        <div className="space-y-1.5">
                          {selected.relative_phones.map((p, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span className="text-gray-500">{p.relacao}:</span>
                              <span className="font-medium text-gray-800">{formatPhone(p.numero)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Financial info */}
                  <div className="border-b pb-4">
                    <h3 className="text-base font-semibold text-gray-800 mb-3">Informações Financeiras</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Empréstimo" value={formatCurrency(selected.loan_amount)} />
                      <Field label="Total a Pagar" value={formatCurrency(selected.total_amount)} />
                      <Field label="Parcelas" value={`${selected.installments}x`} />
                      <Field label="Juros" value={`${selected.interest_rate}%`} />
                    </div>
                  </div>

                  {/* Observations */}
                  <div className="border-b pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-semibold text-gray-800">Observações</h3>
                      {!editingObs && (
                        <button
                          onClick={() => setEditingObs(true)}
                          className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                          Editar
                        </button>
                      )}
                    </div>
                    {editingObs ? (
                      <div className="space-y-2">
                        <textarea
                          value={obsText}
                          onChange={e => setObsText(e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          placeholder="Observações sobre o cadastro..."
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveObs}
                            disabled={savingObs}
                            className="flex items-center gap-1 px-4 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
                          >
                            <Save className="w-3.5 h-3.5" />
                            {savingObs ? 'Salvando...' : 'Salvar'}
                          </button>
                          <button
                            onClick={() => { setEditingObs(false); setObsText(selected.observations || ''); }}
                            className="px-4 py-1.5 text-gray-600 hover:text-gray-800 text-sm hover:bg-gray-100 rounded-md"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {selected.observations || <span className="text-gray-400 italic">Nenhuma observação</span>}
                      </p>
                    )}
                  </div>

                  {/* Installments */}
                  <div>
                    <h3 className="text-base font-semibold text-gray-800 mb-3">Parcelas</h3>
                    <div className="space-y-2">
                      {selected.installments_data.map((inst, i) => (
                        <div key={inst.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{i + 1}ª Parcela</p>
                            <p className="text-xs text-gray-500">
                              Vencimento: {new Date(inst.due_date + 'T00:00:00').toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                          <p className="text-sm font-semibold text-gray-800">{formatCurrency(inst.amount)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right column — documents */}
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-gray-800">Documentos</h3>
                  {selected.contract_files.length > 0 ? (
                    <>
                      {/* Nav */}
                      <div className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                        <button
                          onClick={() => setFileIndex(p => Math.max(0, p - 1))}
                          disabled={fileIndex === 0}
                          className={`p-2 rounded-full ${fileIndex === 0 ? 'text-gray-300' : 'text-blue-600 hover:bg-blue-50'}`}
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <span className="text-sm text-gray-600">
                          {fileIndex + 1} de {selected.contract_files.length}
                        </span>
                        <button
                          onClick={() => setFileIndex(p => Math.min(selected.contract_files.length - 1, p + 1))}
                          disabled={fileIndex === selected.contract_files.length - 1}
                          className={`p-2 rounded-full ${fileIndex === selected.contract_files.length - 1 ? 'text-gray-300' : 'text-blue-600 hover:bg-blue-50'}`}
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>
                      </div>

                      {/* Preview */}
                      <div className="border rounded-lg p-3 bg-gray-50 min-h-[300px] lg:min-h-[500px] flex flex-col items-center justify-center">
                        {getCurrentFile() && (
                          <>
                            <p className="text-xs font-medium text-gray-600 mb-3 text-center break-words">{getCurrentFile()!.file_name}</p>
                            {!fileErrors[getCurrentFile()!.id] ? (
                              isImage(getCurrentFile()!.file_type) ? (
                                <img
                                  src={getCurrentFile()!.file_url}
                                  alt={getCurrentFile()!.file_name}
                                  className="max-w-full max-h-[420px] object-contain rounded-lg shadow-sm"
                                  onError={() => setFileErrors(p => ({ ...p, [getCurrentFile()!.id]: true }))}
                                />
                              ) : isPDF(getCurrentFile()!.file_type) ? (
                                <div className="w-full h-[420px] border rounded-lg overflow-hidden">
                                  <iframe src={getCurrentFile()!.file_url} className="w-full h-full" title={getCurrentFile()!.file_name} />
                                </div>
                              ) : (
                                <div className="text-center">
                                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-3" />
                                  <p className="text-sm text-gray-600">{getCurrentFile()!.file_name}</p>
                                </div>
                              )
                            ) : (
                              <div className="text-center p-8">
                                <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                                <p className="text-sm text-red-600 font-medium">Erro ao carregar arquivo</p>
                              </div>
                            )}
                            <div className="mt-4 flex gap-2">
                              <a
                                href={getCurrentFile()!.file_url}
                                download={getCurrentFile()!.file_name}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                              >
                                <Download className="w-3.5 h-3.5" /> Download
                              </a>
                              <a
                                href={getCurrentFile()!.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-600 text-white rounded-md text-sm hover:bg-gray-700"
                              >
                                <Eye className="w-3.5 h-3.5" /> Abrir
                              </a>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Thumbnails */}
                      <div className="flex gap-2 overflow-x-auto py-2">
                        {selected.contract_files.map((f, i) => (
                          <button
                            key={f.id}
                            onClick={() => setFileIndex(i)}
                            className={`p-2 border rounded-md min-w-[80px] flex-shrink-0 flex flex-col items-center gap-1.5 ${i === fileIndex ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                          >
                            <FileText className="w-6 h-6 text-gray-400" />
                            <span className="text-xs text-gray-600 truncate max-w-[60px]">{f.file_name}</span>
                            {fileErrors[f.id] && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-500 text-center py-8">Nenhum documento disponível</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── Rejection modal ─── */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Recusar Cadastro</h3>
              <p className="text-sm text-gray-500 mb-4">
                Informe o motivo da recusa. O link original sera cancelado.
              </p>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
                placeholder="Ex: Documentos ilegíveis, CPF inconsistente..."
              />
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => { setShowRejectModal(false); setRejectReason(''); }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRejectSubmit}
                  disabled={!rejectReason.trim() || rejecting}
                  className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
                >
                  {rejecting ? <Loader className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4" />}
                  {rejecting ? 'Recusando...' : 'Confirmar Recusa'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── New link modal (post-rejection) ─── */}
        {showNewLinkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <ThumbsDown className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-800">Cadastro Recusado</h3>
                  <p className="text-sm text-gray-500">O link original foi cancelado.</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-5">
                Deseja gerar um novo link de cadastro para que o cliente corrija as informações?
              </p>

              {!newLinkUrl ? (
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowNewLinkModal(false); setRejectedContract(null); }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Nao gerar link
                  </button>
                  <button
                    onClick={handleGenerateNewLink}
                    disabled={generatingLink}
                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    {generatingLink ? <Loader className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />}
                    {generatingLink ? 'Gerando...' : 'Gerar Novo Link'}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <p className="text-xs font-medium text-emerald-700 mb-2">Novo link gerado com sucesso!</p>
                    <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-emerald-200">
                      <p className="text-xs text-gray-600 font-mono truncate flex-1">{newLinkUrl}</p>
                      <button
                        onClick={copyNewLink}
                        className="flex-shrink-0 text-emerald-600 hover:text-emerald-800"
                      >
                        {newLinkCopied
                          ? <CheckCircle2 className="w-4 h-4" />
                          : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    {newLinkCopied && (
                      <p className="text-xs text-emerald-600 mt-1">Link copiado!</p>
                    )}
                  </div>
                  <button
                    onClick={() => { setShowNewLinkModal(false); setRejectedContract(null); setNewLinkUrl(null); }}
                    className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-900"
                  >
                    Fechar
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── helpers ───────────────────────────────────────────────
function Field({ label, value, span }: { label: string; value: string; span?: boolean }) {
  return (
    <div className={span ? 'lg:col-span-2' : ''}>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-900 break-words">{value || '—'}</p>
    </div>
  );
}

